import {
  SecurityDetailsMobile,
  SecurityFavoriteMobileCreationResponse,
  SecurityListMobile,
  SecurityPriceMobile
} from '@wize-mobile-api';
import { AssetTypeEnum, Instrument, PriceSeries, WatchlistItem } from '@qvs/core';
import { Update } from '@ngrx/entity';
import { GetInstrumentDetailsQuery, GetInstrumentLastPriceQuery, LookupStatus, PriceFragment } from '@six-api';
import { isNil } from 'lodash-es';
import { DateUtils } from '~/shared/utils/date.utils';

export class InstrumentMapper {
  public static fromWizeSecurities = (securities: SecurityListMobile[]): Instrument[] =>
    !!securities ? securities.map((security) => this.fromWizeSecurity(security)) : undefined;

  public static fromWizeSecurity = (security: SecurityListMobile): Instrument =>
    !!security
      ? {
          ...InstrumentMapper.fromWizeSecurityCommon(security),
          description: security.description
        }
      : undefined;

  public static fromWizeSecurityDetails = (security: SecurityDetailsMobile): Instrument =>
    !!security
      ? {
          ...InstrumentMapper.fromWizeSecurityCommon(security),
          remark: security.remark,
          subscriptionRule: security.subscriptionRule,
          minimumTradingUnit: security.minimumTradingUnit
        }
      : undefined;

  public static fromWizeAssetClassType = (
    assetClassType: SecurityListMobile.AssetClassTypeEnum | SecurityDetailsMobile.AssetClassTypeEnum
  ): AssetTypeEnum => {
    switch (assetClassType) {
      case SecurityListMobile.AssetClassTypeEnum.BONDS:
      case SecurityListMobile.AssetClassTypeEnum.CONVERTIBLE:
      case SecurityListMobile.AssetClassTypeEnum.BONDS_FLOATING:
        return AssetTypeEnum.BOND;
      case SecurityListMobile.AssetClassTypeEnum.COMMODITIES:
        return AssetTypeEnum.COMMODITY;
      case SecurityListMobile.AssetClassTypeEnum.OPTION:
      case SecurityListMobile.AssetClassTypeEnum.FUTURE:
      case SecurityListMobile.AssetClassTypeEnum.WARRANT:
      case SecurityListMobile.AssetClassTypeEnum.DERIVATIVE:
        return AssetTypeEnum.DERIVATIVE;
      case SecurityListMobile.AssetClassTypeEnum.EQUITY:
        return AssetTypeEnum.EQUITY;
      case SecurityListMobile.AssetClassTypeEnum.EQUITY_ETF:
      case SecurityListMobile.AssetClassTypeEnum.BOND_ETF:
        return AssetTypeEnum.ETF;
      case SecurityListMobile.AssetClassTypeEnum.HEDGE_FUND:
        return AssetTypeEnum.HEDGE_FUND;
      case SecurityListMobile.AssetClassTypeEnum.FUND:
      case SecurityListMobile.AssetClassTypeEnum.MONEY_MARKET:
      case SecurityListMobile.AssetClassTypeEnum.EQUITY_FUND:
      case SecurityListMobile.AssetClassTypeEnum.BOND_FUND:
      case SecurityListMobile.AssetClassTypeEnum.MONETARY_FUND:
      case SecurityListMobile.AssetClassTypeEnum.MIXED_FUND:
      case SecurityListMobile.AssetClassTypeEnum.REAL_ESTATE_FUND:
        return AssetTypeEnum.MUTUAL_FUND;
      case SecurityListMobile.AssetClassTypeEnum.PRECIOUS_METAL:
        return AssetTypeEnum.PRECIOUS_METAL;
      case SecurityListMobile.AssetClassTypeEnum.PRIVATE_EQUITY:
        return AssetTypeEnum.PRIVATE_EQUITY;
      case SecurityListMobile.AssetClassTypeEnum.GUARANTEE:
        return AssetTypeEnum.GUARANTEE;
      case SecurityListMobile.AssetClassTypeEnum.LIQUIDITY:
      case SecurityListMobile.AssetClassTypeEnum.OTHER_SECURITY:
      case SecurityListMobile.AssetClassTypeEnum.INDICE:
      case SecurityListMobile.AssetClassTypeEnum.DEPOSIT_CERTIFICATE:
      case SecurityListMobile.AssetClassTypeEnum.PLEDGE:
      case SecurityListMobile.AssetClassTypeEnum.LIMIT:
      case SecurityListMobile.AssetClassTypeEnum.OTHER:
      case SecurityListMobile.AssetClassTypeEnum.FOREX:
      case SecurityListMobile.AssetClassTypeEnum.MORTGAGE_BACKED_SECURITIES:
      case SecurityListMobile.AssetClassTypeEnum.OTHER_FUND:
      default:
        return AssetTypeEnum.OTHER;
    }
  };

  public static fromWizeSecurityPrices = (prices: SecurityPriceMobile[]): PriceSeries =>
    !!prices
      ? {
          currencyId: prices[0].currencyId,
          source: prices[0].priceSource,
          prices: prices.map((price) => ({ date: price.priceDate, value: price.price }))
        }
      : undefined;

  //TODO 13-Jul-2023/braune: adjust types to cater for null responses
  public static fromWizeSecurityFavoriteMobileCreationResponse = (
    response: SecurityFavoriteMobileCreationResponse
  ): WatchlistItem =>
    !!response
      ? {
          watchlistId: response.favoriteId
        }
      : undefined;

  public static fromSixInstrumentLastPrice = (
    response: GetInstrumentLastPriceQuery,
    instruments: Instrument[]
  ): Update<Instrument>[] =>
    response.listings.map((listing) => {
      const isin = InstrumentMapper.getIsinFromListingId(listing.requestedId);
      const instrument = instruments.find((instr) => instr.isin === isin);
      const currentPrice = listing?.marketData?.intradaySnapshot?.last;
      if (listing.lookupStatus !== LookupStatus.Found || isNil(currentPrice)) {
        return {
          id: instrument.id as string,
          changes: {}
        };
      }
      return {
        id: instrument.id as string,
        changes: {
          currentPrice: {
            amount: {
              currency: listing?.lookup?.listingCurrency,
              value: currentPrice.value
            },
            timestamp: currentPrice.timestamp
          }
        }
      };
    });

  public static fromSixInstrumentDetails = (response: GetInstrumentDetailsQuery, instrument: Instrument): Update<Instrument> =>
    response.listings.map((listing) => {
      if (listing.lookupStatus !== LookupStatus.Found) {
        return {
          id: instrument.id as string,
          changes: {}
        };
      }
      const intraDayData = listing?.marketData?.intradaySnapshot;
      const prices: PriceFragment[] = listing?.marketData?.intradayHistory?.summary;
      return {
        id: instrument.id as string,
        changes: {
          marketCapitalisation: {
            value: intraDayData?.marketCapitalisation?.value,
            currency: listing?.lookup?.listingCurrency ?? instrument?.endOfDayPrice?.amount?.currency
          },
          yieldToMaturity: intraDayData?.yieldToMaturity?.value,
          priceEarningsRatioEstimated: intraDayData?.priceEarningsRatioEstimated?.value,
          turnover: {
            value: intraDayData?.turnover?.value,
            currency: listing?.lookup?.listingCurrency ?? instrument?.endOfDayPrice?.amount?.currency
          },
          priceData: {
            p1d: InstrumentMapper.fromSixInstrumentPrices(prices, listing?.lookup?.listingCurrency)
          }
        }
      };
    })[0];

  private static readonly fromSixInstrumentPrices = (prices: PriceFragment[], currency: string): PriceSeries =>
    !!prices
      ? {
          currencyId: currency,
          source: 'SIX',
          prices: prices
            .map((price) => ({ date: price.intervalFrom, value: price.last }))
            .sort((a, b) => (DateUtils.new(a.date).isBefore(b.date) ? -1 : 1))
        }
      : undefined;

  private static readonly getIsinFromListingId = (requestedId: string): string => requestedId.split('_')[0];

  private static readonly fromWizeSecurityCommon = (security: SecurityListMobile | SecurityDetailsMobile): Instrument =>
    !!security
      ? {
          id: security.id,
          isin: security.identifications?.ISIN,
          ticker: security.identifications?.REUTERS,
          assetType: InstrumentMapper.fromWizeAssetClassType(security.assetClassType),
          shortDescription: security.shortDescription,
          isTradingAllowed: security.tradingAllowed,
          watchlistId: security.favoriteId,
          endOfDayPrice:
            !!security.price && !!security.priceDate
              ? {
                  amount: {
                    value: security.price,
                    currency: security.currencyId
                  },
                  timestamp: security.priceDate
                }
              : undefined
        }
      : undefined;
}
